import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, IdentificationCard, XCircle } from '@phosphor-icons/react';
import QRCode from 'qrcode';
import { getPublicMemberCard } from '@/api/membercards_public';
import { ApiError } from '@/api/client';

// Public member-card scan page. Routed at /m/c/:token, OUTSIDE RequireAuth:
// the public_token in the URL is the credential. Mirrors the PublicInvoice
// shape — read-only, theme from the org's colors, surfaces only the
// sanitized payment status.
export function PublicMemberCard() {
  const { token = '' } = useParams();
  const { t, i18n } = useTranslation('common');

  const cardQuery = useQuery({
    queryKey: ['public-membercard', token],
    queryFn: () => getPublicMemberCard(token),
    enabled: !!token,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 2;
    },
  });

  if (cardQuery.isPending) {
    return <Shell tone="default">{t('memberCards.loading')}</Shell>;
  }
  if (cardQuery.error instanceof ApiError && cardQuery.error.status === 404) {
    return <Shell tone="default">{t('memberCards.publicNotFound')}</Shell>;
  }
  if (cardQuery.error) {
    return <Shell tone="default">{t('memberCards.loadError')}</Shell>;
  }
  const data = cardQuery.data!;
  const paid = data.status.paid;
  const tone = paid ? 'paid' : 'unpaid';
  const yearLabel = paid
    ? t('memberCards.publicPaid', { year: data.status.fiscal_year })
    : t('memberCards.publicUnpaid', { year: data.status.fiscal_year });
  const dateFmt = new Intl.DateTimeFormat(i18n.language || 'en', { dateStyle: 'medium' });
  const paidAt = data.status.paid_at ? dateFmt.format(new Date(data.status.paid_at)) : null;

  return (
    <Shell tone={tone}>
      <div
        className="rounded-3xl p-8 shadow-2xl"
        style={{
          backgroundColor: data.template.background_color || '#26272B',
          color: data.template.text_color || '#FFFDD8',
          maxWidth: 480,
          width: '100%',
        }}
      >
        <header className="flex items-center gap-3 mb-6">
          {data.org.logo_url ? (
            <img src={data.org.logo_url} alt="" className="h-10 w-10 rounded-full bg-white/10 object-contain p-1" />
          ) : (
            <IdentificationCard size={28} weight="bold" />
          )}
          <div>
            <p className="text-xs uppercase tracking-wider opacity-70">{t('memberCards.title')}</p>
            <h1 className="text-xl font-bold leading-tight">{data.org.name}</h1>
          </div>
        </header>

        <p className="text-3xl font-bold leading-tight mb-1">{data.member.display_name}</p>
        <p className="text-sm opacity-80 mb-6">{t(`memberCards.pii.${camelCase(data.member.pii_level)}`)}</p>

        <div
          className="flex items-center gap-3 rounded-2xl px-4 py-3"
          style={{
            backgroundColor: paid ? data.template.accent_color : '#FFFDD8',
            color: paid ? data.template.background_color : '#26272B',
          }}
        >
          {paid ? <CheckCircle size={28} weight="fill" /> : <XCircle size={28} weight="bold" />}
          <div>
            <p className="font-bold">{yearLabel}</p>
            {paidAt ? <p className="text-xs opacity-80">{paidAt}</p> : null}
          </div>
        </div>
      </div>
    </Shell>
  );
}

function camelCase(s: string): 'fullName' | 'firstName' | 'memberId' {
  if (s === 'first_name') return 'firstName';
  if (s === 'member_id') return 'memberId';
  return 'fullName';
}

interface ShellProps {
  tone: 'default' | 'paid' | 'unpaid';
  children: React.ReactNode;
}

function Shell({ children }: ShellProps) {
  // Mobile-first: the card centers in the viewport, with comfortable
  // padding on small screens. Black neutral surface gives the card
  // visual focus regardless of org theme.
  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      {children}
    </main>
  );
}

// Small helper consumers might want — kept here so a future "scan kiosk"
// can prefer this over making its own QR client-side. Not exported but
// trivially shareable if needed.
export async function _renderQRDataURL(text: string): Promise<string> {
  return QRCode.toDataURL(text, { errorCorrectionLevel: 'M', margin: 1, scale: 6 });
}
